/**
 * SA per-demo status query hook (B-11).
 *
 * Endpoint: GET /admin/demo/{slug}/status
 */
import { useQuery } from "@tanstack/react-query";
import { apiClient } from "@/lib/apiClient";
import { ApiError } from "@/lib/apiError";

interface DemoSlugStatusResponse {
  slug: string;
  exists: boolean;
  last_reset_at: string | null;
  admin_email: string | null;
  candidate_count: number;
  ranking_count: number;
  /** D18 — lets the demo-detail page reach SA tenant user-mgmt + MFA toggle. */
  tenant_id: string | null;
  /** D18 — current MFA-policy state for the demo tenant. */
  mfa_required: boolean;
  /** P1 — current SA-configured Intelligence dashboard variant. */
  dashboard_persona: "agency" | "in_house";
  jd_count: number;
  expected_jd_count: number;
  expected_candidate_count: number;
  expected_ranking_count: number;
  /**
   * False when the tenant is HALF-SEEDED. A demo reset is a >4-minute
   * synchronous run ending in a live-AI loop; if it dies partway the
   * wipe+provision has already committed, so the broken tenant stays live and
   * login-able with unremarkable-looking counts. Compare against the
   * `expected_*` fields to show the shortfall.
   */
  seed_complete: boolean;
}

export function useAdminDemo(slug: string) {
  return useQuery<DemoSlugStatusResponse, ApiError>({
    queryKey: ["admin", "demo", slug, "status"],
    queryFn: async () => {
      const r = await apiClient.get<DemoSlugStatusResponse>(
        `/admin/demo/${slug}/status`,
      );
      return r.data;
    },
    enabled: slug.length > 0,
  });
}
